<template>
  <div>
    <Skeleton v-if="!labelsLoaded" />

    <div v-else class="rhg-instructions-map rhg-instructions-map__container">
      <div v-if="isMapInitialized" class="rhg-instructions-map__menu-wrapper">
        <Menu
          :googleMaps="googleMaps"
          :travelModeOptions="travelModeOptions"
          :walkingTime="walkingTime"
          :transitTime="transitTime"
          :drivingTime="drivingTime"
          :timesLoaded="timesLoaded"
          :centerCoordinates="mapData.coordinates"
          @onOriginPontSelected="handleOriginPointSelected"
          @onTravelModeSelection="handleTravelModeSelection"
          @onDeleteSearch="handleMapReset"
        />
        <Button v-if="showButtonOnDesktop" :showMap="showMap" @onViewComponentChange="handleViewComponentChange" :disableButton="legs.length === 0"></Button>
      </div>

      <InstructionsMap
        v-show="mapData && showMap"
        id="instructions-map"
        :mapData="mapData"
        :showMap="showMap"
        :pointOfOrigin="pointOfOriginCoordinates"
        :unitSystem="unitSystem"
        :currentTravelModeSelected="currentTravelModeSelected"
        @onGoogleInit="handleGoogleInit"
        :resetRoutes="resetRoutes"
        @onDrivingTimeDurationChange="handleDrivingTimeDurationChange"
        @onWalkingTimeDurationChange="handleWalkingTimeDurationChange"
        @onTransitTimeDurationChange="handleTransitTimeDurationChange"
        @onDirectionsChange="handleDirectionsChange"
      />
      <Instructions
        v-if="!showMap"
        :routeSteps="legs[0]"
        :originName="pointOfOriginName"
        :unitSystemOptions="unitSystemOptions"
        @onUnitSystemSelection="handleUnitSystemChange"
      />
      <div v-if="!showButtonOnDesktop" :class="{'rhg-instructions-map__button-wrapper--border-top': !showMap}" class="rhg-instructions-map__button-wrapper">
        <Button class="button-mobile" :showMap="showMap" @onViewComponentChange="handleViewComponentChange" :disableButton="legs.length === 0"></Button>
      </div>
    </div>
  </div>
</template>

<script>
import InstructionsMap from '@/components/rhg-instructions-map/InstructionsMap'
import Menu from '@/components/rhg-instructions-map/Menu'
import Instructions from '@/components/rhg-instructions-map/Instructions'
import Button from '@/components/rhg-instructions-map/components/Button'
import Skeleton from '@/components/rhg-instructions-map/InstructionsMapSkeleton'

export default {
  name: 'InstructionsMapView',
  components: {
    InstructionsMap,
    Menu,
    Button,
    Instructions,
    Skeleton
  },
  props: {
    labelsLoaded: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    mapData: null,
    googleMaps: null,
    travelModeOptions: null,
    unitSystemOptions: null,
    isMapInitialized: false,
    showButtonOnDesktop: null,
    pointOfOriginCoordinates: {},
    pointOfOriginName: '',
    unitSystem: 0,
    currentTravelModeSelected: 'DRIVING',
    drivingTime: null,
    walkingTime: null,
    transitTime: null,
    timesLoaded: {
      driving: false,
      transit: false,
      walking: false
    },
    showMap: true,
    legs: [],
    resetRoutes: false
  }),
  created() {
    if (global.rhgInstructionsMap) {
      this.mapData = global.rhgInstructionsMap
    }
    this.shouldShowButtonDesktop()

    window.addEventListener('resize', () => this.shouldShowButtonDesktop())

    this.$store.dispatch('sendAnalyticsView')
  },
  destroyed() {
    window.addEventListener('resize', () => this.shouldShowButtonDesktop())
  },
  methods: {
    handleGoogleInit(google) {
      this.isMapInitialized = true
      this.googleMaps = google.maps
      this.travelModeOptions = google.maps.TravelMode
      this.unitSystemOptions = google.maps.UnitSystem
    },
    handleOriginPointSelected(pointOfOrigin) {
      this.pointOfOriginCoordinates = pointOfOrigin.coordinates
      this.pointOfOriginName = pointOfOrigin.pointName
    },
    shouldShowButtonDesktop() {
      this.showButtonOnDesktop = window.innerWidth > 991
    },
    handleWalkingTimeDurationChange(timeDuration) {
      this.timesLoaded.walking = true
      this.walkingTime = timeDuration
    },
    handleDrivingTimeDurationChange(timeDuration) {
      this.timesLoaded.driving = true
      this.drivingTime = timeDuration
    },
    handleTransitTimeDurationChange(timeDuration) {
      this.timesLoaded.transit = true
      this.transitTime = timeDuration
    },
    handleTravelModeSelection(value) {
      this.currentTravelModeSelected = value
    },
    handleViewComponentChange(value) {
      this.showMap = value
    },
    handleDirectionsChange(legs) {
      this.legs = legs
    },
    handleUnitSystemChange(value) {
      this.unitSystem = value
    },
    handleMapReset() {
      this.pointOfOriginCoordinates = {}
      this.pointOfOriginName = ''
      this.unitSystem = 0
      this.currentTravelModeSelected = 'DRIVING'
      this.drivingTime = null
      this.walkingTime = null
      this.transitTime = null
      this.legs = []
      this.resetRoutes = true
      this.showMap = true
      this.timesLoaded = {
        driving: false,
        transit: false,
        walking: false
      }

      this.$nextTick(() => {
        this.resetRoutes = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/global/colors.scss";
.button-mobile {
  margin: 0 auto;
}
.rhg-instructions-map {
  &__skeleton-container {
    min-height: 656px;
    max-width: 1440px;
    background: color('white');

    @media (min-width: 992px) {
      min-height: 788px;
    }
  }

  &__container {
    min-height: 656px;
    max-width: 1440px;
    background: color('white');
    border-radius: 8px;

    @media (min-width: 992px) {
      min-height: 788px;
    }
  }

  &__menu-wrapper {
    @media (min-width: 992px) {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 36px;
      padding: 39px 24px 34px 24px;
    }
  }

  &__button-wrapper {
    padding: 16px;
    align-self: center;
    width: 100%;
    background-color: white;
    position: sticky;
    z-index: 10;

    &--border-top {
      border-top: 1px solid rgba(color('black-light'), 0.3);
      box-shadow: 0px -6px 0px 0px rgb(255, 255, 255);
      border-radius: 8px;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }
}
</style>
